// src/hooks/useVersionChecker.tsx
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

interface VersionInfo {
  version: string;
}

const STORAGE_KEY = "app_version";

// random comment to cause a version update

export function useVersionChecker() {
  const location = useLocation();
  const currentVersion = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Get version from localStorage
  const getStoredVersion = useCallback(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      console.warn("Failed to read version from localStorage:", err);
      return null;
    }
  }, []);

  // Store version in localStorage
  const setStoredVersion = useCallback((version: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, version);
    } catch (err) {
      console.warn("Failed to store version in localStorage:", err);
    }
  }, []);

  // Initialize current version from localStorage (only once)
  if (currentVersion.current == null) {
    currentVersion.current = getStoredVersion();
  }

  const reloadPage = useCallback(() => {
    window.location.reload();
  }, []);

  const checkVersion = useCallback(async () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const res = await fetch("/version.json", {
        cache: "no-store",
        signal: abortControllerRef.current.signal,
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data: VersionInfo = await res.json();

      if (!currentVersion.current) {
        // First time - store the version both in memory and localStorage
        currentVersion.current = data.version;
        setStoredVersion(data.version);
      } else if (data.version !== currentVersion.current) {
        // Update stored version immediately so other tabs know
        setStoredVersion(data.version);
        currentVersion.current = data.version;
        reloadPage();
      }
    } catch (err) {
      // Only log errors that aren't due to aborted requests
      if (err instanceof Error && err.name !== "AbortError") {
        console.warn("Version check failed:", err.message);
      }
    }
  }, [reloadPage, setStoredVersion]);

  const forceRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  // Check version on every page navigation
  useEffect(() => {
    // Always check version on navigation, even on first load
    checkVersion();
  }, [location.pathname, checkVersion]);

  return {
    forceRefresh,
  };
}
